/**
 * Table of Contents Component
 *
 * Auto-generated TOC with:
 * - Scroll spy highlighting current section
 * - Smooth scroll navigation
 * - Responsive: inline collapsed on mobile, sticky sidebar on desktop
 * - Save button with conversion-focused states
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Collapse,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import {
  IconBookmark,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconList,
  IconLoader2,
} from '@tabler/icons-react'
import type { TemplateSection, TemplateStep } from '../types'

type SaveButtonState = 'default' | 'engaged' | 'saving' | 'success'

interface ProjectGuideTOCProps {
  steps: Array<TemplateStep>
  sections?: Array<TemplateSection>
  isCustomized: boolean
  onSave: () => void
  isSaving?: boolean
  isSaved?: boolean
  onTocNavigate?: (sectionId: string) => void
}

interface TOCEntry {
  id: string
  label: string
  type: 'page-section' | 'project-section' | 'step'
  icon?: string
  sectionSlug?: string
  stepCount?: number
}

function buildTOCEntries(
  steps: Array<TemplateStep>,
  sections?: Array<TemplateSection>
): Array<TOCEntry> {
  const entries: Array<TOCEntry> = [
    { id: 'overview', label: 'Overview', type: 'page-section' },
  ]

  if (sections && sections.length > 0) {
    // Group steps by section
    const sortedSections = [...sections].sort((a, b) => a.order - b.order)
    let stepIndex = 0

    for (const section of sortedSections) {
      const sectionSteps = steps
        .filter((s) => s.sectionSlug === section.slug)
        .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
      if (sectionSteps.length === 0) continue

      // Add section header
      entries.push({
        id: `section-${section.slug}`,
        label: section.title,
        type: 'project-section',
        icon: section.icon,
        sectionSlug: section.slug,
        stepCount: sectionSteps.length,
      })

      // Add steps under this section
      for (const step of sectionSteps) {
        entries.push({
          id: `step-${stepIndex}`,
          label: `${stepIndex + 1}. ${step.title}`,
          type: 'step',
          sectionSlug: section.slug,
        })
        stepIndex++
      }
    }

    // Add orphan steps (no sectionSlug), sorted
    const orphanSteps = steps
      .filter((s) => !s.sectionSlug)
      .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
    for (const step of orphanSteps) {
      entries.push({
        id: `step-${stepIndex}`,
        label: `${stepIndex + 1}. ${step.title}`,
        type: 'step',
      })
      stepIndex++
    }
  } else {
    // No sections - flat list
    steps.forEach((step, index) => {
      entries.push({
        id: `step-${index}`,
        label: `${index + 1}. ${step.title}`,
        type: 'step',
      })
    })
  }

  entries.push({ id: 'faq', label: 'FAQ', type: 'page-section' })

  return entries
}

function getSaveButtonState(
  isCustomized: boolean,
  isSaving: boolean,
  isSaved: boolean
): SaveButtonState {
  if (isSaved) return 'success'
  if (isSaving) return 'saving'
  if (isCustomized) return 'engaged'
  return 'default'
}

export function ProjectGuideTOC({
  steps,
  sections,
  isCustomized,
  onSave,
  isSaving = false,
  isSaved = false,
  onTocNavigate,
}: ProjectGuideTOCProps) {
  const [activeId, setActiveId] = useState<string>('overview')
  const [opened, { toggle }] = useDisclosure(false)
  // Track which TOC sections are expanded (start all collapsed for cleaner UX)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const observerRef = useRef<IntersectionObserver | null>(null)
  // Use initialValue: false to ensure SSR renders mobile view consistently
  // This prevents hydration mismatch since server always renders mobile
  const isDesktop = useMediaQuery('(min-width: 1024px)', false, {
    getInitialValueInEffect: false,
  })

  const entries = buildTOCEntries(steps, sections)
  
  // Auto-expand section containing active step
  useEffect(() => {
    const activeEntry = entries.find((e) => e.id === activeId)
    if (activeEntry?.sectionSlug && !expandedSections.has(activeEntry.sectionSlug)) {
      setExpandedSections((prev) => new Set([...prev, activeEntry.sectionSlug!]))
    }
  }, [activeId, entries, expandedSections])
  
  const toggleTocSection = useCallback((sectionSlug: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev)
      if (next.has(sectionSlug)) {
        next.delete(sectionSlug)
      } else {
        next.add(sectionSlug)
      }
      return next
    })
  }, [])
  const saveState = getSaveButtonState(isCustomized, isSaving, isSaved)

  // Set up IntersectionObserver for scroll spy
  // Use a small delay to ensure DOM is ready after hydration
  useEffect(() => {
    const handleIntersect = (observerEntries: Array<IntersectionObserverEntry>) => {
      for (const entry of observerEntries) {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id)
          break
        }
      }
    }

    // Small delay to allow hydration to complete
    const timeoutId = setTimeout(() => {
      observerRef.current = new IntersectionObserver(handleIntersect, {
        rootMargin: '-20% 0px -60% 0px',
        threshold: 0,
      })

      // Observe all sections
      const sectionElements = document.querySelectorAll('[data-toc-section]')
      sectionElements.forEach((section) => {
        observerRef.current?.observe(section)
      })
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      observerRef.current?.disconnect()
    }
  }, [steps.length])

  const handleClick = useCallback(
    (id: string) => {
      onTocNavigate?.(id)
      const element = document.getElementById(id)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    [onTocNavigate]
  )

  const saveButtonProps = {
    default: {
      variant: 'outline' as const,
      color: 'gray',
      leftSection: <IconBookmark size={16} />,
      children: 'Save Guide',
    },
    engaged: {
      variant: 'filled' as const,
      color: 'primary',
      leftSection: <IconBookmark size={16} />,
      children: 'Save Your Plan',
    },
    saving: {
      variant: 'filled' as const,
      color: 'primary',
      leftSection: <IconLoader2 size={16} className="animate-spin" />,
      children: 'Saving...',
      disabled: true,
    },
    success: {
      variant: 'filled' as const,
      color: 'green',
      leftSection: <IconCheck size={16} />,
      children: 'Saved!',
    },
  }

  // Group entries for rendering
  const renderEntries = () => {
    const result: Array<React.ReactNode> = []
    let i = 0
    
    while (i < entries.length) {
      const entry = entries[i]
      
      if (entry.type === 'page-section') {
        // Page-level sections (Overview, FAQ) - render directly
        result.push(
          <UnstyledButton
            key={entry.id}
            onClick={() => handleClick(entry.id)}
            aria-label={`Jump to ${entry.label}`}
            aria-current={activeId === entry.id ? 'location' : undefined}
            style={{
              padding: '4px 8px',
              borderRadius: 4,
              backgroundColor:
                activeId === entry.id
                  ? 'var(--mantine-color-primary-light)'
                  : 'transparent',
            }}
          >
            <Text
              size="sm"
              c={activeId === entry.id ? 'primary' : 'dimmed'}
              fw={activeId === entry.id ? 500 : 400}
            >
              {entry.label}
            </Text>
          </UnstyledButton>
        )
        i++
      } else if (entry.type === 'project-section') {
        // Project section with nested steps
        const sectionSlug = entry.sectionSlug!
        const isExpanded = expandedSections.has(sectionSlug)
        
        // Collect all steps for this section
        const sectionSteps: Array<TOCEntry> = []
        let j = i + 1
        while (j < entries.length && entries[j].type === 'step' && entries[j].sectionSlug === sectionSlug) {
          sectionSteps.push(entries[j])
          j++
        }
        
        // Check if any step in this section is active
        const hasActiveStep = sectionSteps.some((s) => s.id === activeId)
        
        result.push(
          <Box key={entry.id}>
            <UnstyledButton
              onClick={() => toggleTocSection(sectionSlug)}
              aria-expanded={isExpanded}
              style={{
                padding: '4px 8px',
                borderRadius: 4,
                width: '100%',
                backgroundColor: hasActiveStep && !isExpanded
                  ? 'var(--mantine-color-primary-light)'
                  : 'transparent',
              }}
            >
              <Group gap="xs" justify="space-between" wrap="nowrap">
                <Group gap="xs" wrap="nowrap">
                  {entry.icon && <Text size="sm">{entry.icon}</Text>}
                  <Text
                    size="sm"
                    c={hasActiveStep ? 'primary' : 'dimmed'}
                    fw={hasActiveStep ? 500 : 400}
                  >
                    {entry.label}
                  </Text>
                </Group>
                <Group gap={4} wrap="nowrap">
                  <Text size="xs" c="dimmed">
                    {entry.stepCount}
                  </Text>
                  {isExpanded ? (
                    <IconChevronUp size={14} color="var(--mantine-color-dimmed)" />
                  ) : (
                    <IconChevronDown size={14} color="var(--mantine-color-dimmed)" />
                  )}
                </Group>
              </Group>
            </UnstyledButton>
            
            <Collapse in={isExpanded}>
              <Stack gap={2} pl="md" mt={4}>
                {sectionSteps.map((stepEntry) => (
                  <UnstyledButton
                    key={stepEntry.id}
                    onClick={() => handleClick(stepEntry.id)}
                    aria-label={`Jump to ${stepEntry.label}`}
                    aria-current={activeId === stepEntry.id ? 'location' : undefined}
                    style={{
                      padding: '2px 8px',
                      borderRadius: 4,
                      backgroundColor:
                        activeId === stepEntry.id
                          ? 'var(--mantine-color-primary-light)'
                          : 'transparent',
                    }}
                  >
                    <Text
                      size="xs"
                      c={activeId === stepEntry.id ? 'primary' : 'dimmed'}
                      fw={activeId === stepEntry.id ? 500 : 400}
                      lineClamp={1}
                    >
                      {activeId === stepEntry.id && '● '}
                      {stepEntry.label}
                    </Text>
                  </UnstyledButton>
                ))}
              </Stack>
            </Collapse>
          </Box>
        )
        
        i = j // Skip past the steps we just rendered
      } else {
        // Orphan step (no section) - render directly
        result.push(
          <UnstyledButton
            key={entry.id}
            onClick={() => handleClick(entry.id)}
            aria-label={`Jump to ${entry.label}`}
            aria-current={activeId === entry.id ? 'location' : undefined}
            style={{
              padding: '4px 8px',
              borderRadius: 4,
              backgroundColor:
                activeId === entry.id
                  ? 'var(--mantine-color-primary-light)'
                  : 'transparent',
            }}
          >
            <Text
              size="sm"
              c={activeId === entry.id ? 'primary' : 'dimmed'}
              fw={activeId === entry.id ? 500 : 400}
            >
              {activeId === entry.id && '● '}
              {entry.label}
            </Text>
          </UnstyledButton>
        )
        i++
      }
    }
    
    return result
  }

  const tocContent = (
    <Stack gap="xs">
      {renderEntries()}

      <Box mt="md">
        <Tooltip
          label={
            saveState === 'default'
              ? 'Customize your plan, then save it'
              : undefined
          }
          disabled={saveState !== 'default'}
        >
          <Button
            fullWidth
            onClick={onSave}
            {...saveButtonProps[saveState]}
          />
        </Tooltip>
      </Box>
    </Stack>
  )

  // Desktop: Sticky sidebar
  if (isDesktop) {
    return (
      <Paper
        withBorder
        p="md"
        radius="md"
        style={{
          position: 'sticky',
          top: 80,
          maxHeight: 'calc(100vh - 100px)',
          overflowY: 'auto',
        }}
      >
        <Text fw={600} size="sm" mb="md">
          In This Guide
        </Text>
        {tocContent}
      </Paper>
    )
  }

  // Mobile: Inline collapsed
  return (
    <Paper withBorder p="sm" radius="md">
      <UnstyledButton
        onClick={toggle}
        aria-expanded={opened}
        aria-controls="toc-content"
        aria-label={opened ? 'Collapse table of contents' : 'Expand table of contents'}
        style={{ width: '100%' }}
      >
        <Group justify="space-between">
          <Group gap="xs">
            <IconList size={16} />
            <Text fw={600} size="sm">
              Table of Contents
            </Text>
          </Group>
          <Box c="dimmed" style={{ display: 'flex', alignItems: 'center' }}>
            {opened ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
          </Box>
        </Group>
      </UnstyledButton>

      <Collapse in={opened}>
        <Box id="toc-content" mt="sm" pt="sm" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
          {tocContent}
        </Box>
      </Collapse>
    </Paper>
  )
}
